import { ROLE_CABINET_ROUTES, CATEGORY_CABINET_ROUTES } from './roleRoutes';
import { routes } from './routes';
import { storageKeys } from './config';
import { readStorage, writeStorage, removeStorage } from './storage';
import type { RoleCode } from '@/types';

/** History / location.state payload for cabinet navigation (HTML ov7Role parity). */
export type ZaNavState = {
  role?: string;
  category?: string;
  /** Owner category preview — never switch activeRole to this code. */
  preview?: string;
  returnTo?: string;
};

export type ZaLocationState = {
  zaNav?: ZaNavState;
  [key: string]: unknown;
};

const RETURN_TO_KEY = storageKeys.navReturnTo;

/** Shared /app surfaces that are not a role/category cabinet. */
const NON_CABINET_PREFIXES = [
  routes.profile,
  routes.assistant,
  '/app/consultant',
  routes.requests,
  routes.newRequest,
  routes.journals,
  routes.people,
  routes.audit,
  routes.security,
  routes.notifications,
  '/app/research-tools',
] as const;

/** Path prefixes → preferred role/category codes (longest match wins). */
const PATH_CODES: Array<{ path: string; codes: string[] }> = (() => {
  const byPath = new Map<string, string[]>();
  const add = (code: string, path: string | undefined) => {
    if (!path || path === routes.home) return;
    const list = byPath.get(path) ?? [];
    if (!list.includes(code)) list.push(code);
    byPath.set(path, list);
  };
  for (const [code, path] of Object.entries(ROLE_CABINET_ROUTES)) add(code, path);
  for (const [code, path] of Object.entries(CATEGORY_CABINET_ROUTES)) add(code, path);
  return [...byPath.entries()]
    .map(([path, codes]) => ({ path, codes }))
    .sort((a, b) => b.path.length - a.path.length);
})();

export const isWorkspaceCabinetPath = (pathname: string): boolean => {
  if (!pathname.startsWith('/app')) return false;
  if (pathname === routes.app) return false;
  return !NON_CABINET_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
};

/**
 * Resolve role/category code(s) for a cabinet pathname.
 * Prefer the user's current activeRole when it matches the path (ambiguous aliases).
 */
export const resolveCodesForPath = (pathname: string): string[] => {
  for (const entry of PATH_CODES) {
    if (pathname === entry.path || pathname.startsWith(`${entry.path}/`)) {
      return entry.codes;
    }
  }
  return [];
};

export const pickRoleForPath = (
  pathname: string,
  userRoles: readonly string[],
  preferredRole?: string | null,
): RoleCode | null => {
  const codes = resolveCodesForPath(pathname);
  if (!codes.length) return null;
  if (preferredRole && codes.includes(preferredRole) && userRoles.includes(preferredRole)) {
    return preferredRole as RoleCode;
  }
  for (const code of codes) {
    if (userRoles.includes(code)) return code as RoleCode;
  }
  return null;
};

export const rememberReturnTo = (pathname: string): void => {
  if (!isWorkspaceCabinetPath(pathname)) return;
  writeStorage(RETURN_TO_KEY, pathname);
};

export const readReturnTo = (): string | null => {
  const value = readStorage<string | null>(RETURN_TO_KEY, null);
  return typeof value === 'string' && value.startsWith('/app') ? value : null;
};

export const clearReturnTo = (): void => {
  removeStorage(RETURN_TO_KEY);
};

export const getReturnToFromLocation = (
  state: unknown,
  fallbackPath?: string,
): string | null => {
  const zaNav = (state as ZaLocationState | null)?.zaNav;
  if (zaNav?.returnTo && zaNav.returnTo.startsWith('/app')) return zaNav.returnTo;
  return readReturnTo() ?? (fallbackPath && isWorkspaceCabinetPath(fallbackPath) ? fallbackPath : null);
};

export const buildZaNavState = (input: {
  pathname: string;
  role?: string | null;
  category?: string | null;
  preview?: string | null;
  returnTo?: string | null;
}): ZaNavState => {
  const codes = resolveCodesForPath(input.pathname);
  const role = input.role ?? codes[0];
  const category = input.category ?? (codes.find((c) => c !== role) || codes[0]);
  const returnTo =
    input.returnTo ??
    (isWorkspaceCabinetPath(input.pathname) ? input.pathname : readReturnTo() ?? undefined);
  return {
    ...(role ? { role } : {}),
    ...(category ? { category } : {}),
    ...(input.preview ? { preview: input.preview } : {}),
    ...(returnTo ? { returnTo } : {}),
  };
};

export const mergeLocationState = (
  current: unknown,
  zaNav: ZaNavState,
): ZaLocationState => ({
  ...(typeof current === 'object' && current ? (current as ZaLocationState) : {}),
  zaNav: {
    ...((current as ZaLocationState | null)?.zaNav ?? {}),
    ...zaNav,
  },
});

/** Navigate options for opening a role/category cabinet. */
export const cabinetNavigateOptions = (
  href: string,
  opts?: {
    role?: string;
    category?: string;
    preview?: string;
    currentState?: unknown;
  },
) => ({
  state: mergeLocationState(
    opts?.currentState,
    buildZaNavState({
      pathname: href,
      role: opts?.role,
      category: opts?.category,
      preview: opts?.preview,
      returnTo: href,
    }),
  ),
});

/** Navigate options for leaving a cabinet toward Home (keeps returnTo). */
export const homeNavigateOptions = (fromPathname: string, currentState?: unknown) => {
  rememberReturnTo(fromPathname);
  const returnTo = getReturnToFromLocation(currentState, fromPathname) ?? fromPathname;
  return {
    state: mergeLocationState(currentState, {
      returnTo: isWorkspaceCabinetPath(returnTo) ? returnTo : undefined,
    }),
  };
};
